import React, { useState, useEffect, useRef } from 'react';
import { UploadCloud, FileText, Download, Trash2, Eye, ExternalLink } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { FileRecord } from '../../../shared/types.ts';
import { FileProofingModal } from './FileProofingModal.tsx';
import { DocumentPreviewModal } from '../common/DocumentPreviewModal.tsx';
import { describeUploadFailure } from '../../services/uploadErrors.ts';

interface ProjectFilesViewProps {
  projectId: string;
}

export const ProjectFilesView: React.FC<ProjectFilesViewProps> = ({ projectId }) => {
  const { token, user } = useAuth();
  const [files, setFiles] = useState<FileRecord[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState<FileRecord | null>(null);
  const [proofingFile, setProofingFile] = useState<FileRecord | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchFiles = async () => {
    if (!token) return;
    try {
      const res = await fetch(`/api/files/project/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setFiles(data.files);
      }
    } catch (err) {
      console.error('Failed to load project files:', err);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, [projectId, token]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected || !token) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', selected);
    formData.append('projectId', projectId);

    try {
      const res = await fetch('/api/files/upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        await fetchFiles();
      } else {
        alert(await describeUploadFailure(res, selected.name));
      }
    } catch (err: any) {
      console.error('File upload failed:', err);
      alert(`Could not upload "${selected.name}": ${err.message || 'network error'}`);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    if (!token || !confirm('Are you sure you want to delete this file?')) return;

    try {
      const res = await fetch(`/api/files/${fileId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setFiles((prev) => prev.filter((f) => f.id !== fileId));
      }
    } catch (err) {
      console.error('Failed to delete file:', err);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6">
      {/* Upload Drop Zone */}
      <div
        onClick={() => fileInputRef.current?.click()}
        className="border-2 border-dashed border-surface-200 dark:border-surface-700 hover:border-brand-500/60 dark:hover:border-brand-500/60 rounded-2xl p-8 text-center cursor-pointer bg-white dark:bg-surface-900 transition-colors group"
      >
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileUpload}
          className="hidden"
          disabled={isUploading}
        />
        <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-brand-500/10 text-brand-600 dark:text-brand-400 flex items-center justify-center group-hover:scale-105 transition-transform">
          <UploadCloud className="w-6 h-6" />
        </div>
        <h4 className="text-sm font-semibold text-surface-900 dark:text-surface-100">
          {isUploading ? 'Uploading file...' : 'Click or drop files here to upload'}
        </h4>
        <p className="text-xs text-surface-500 mt-1">
          Support for images, PDFs, spreadsheets, presentations, and documents up to 100MB
        </p>
      </div>

      {/* Files Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {files.map((file) => (
          <div
            key={file.id}
            className="p-4 bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-800 shadow-card hover:shadow-elevated transition-all flex flex-col justify-between"
          >
            <div className="flex items-start justify-between space-x-3 mb-3">
              <div className="flex items-center space-x-3 truncate">
                <div className="w-10 h-10 rounded-xl bg-brand-50 dark:bg-brand-950/40 text-brand-600 dark:text-brand-400 flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5" />
                </div>
                <div className="truncate">
                  <h5 className="text-xs font-semibold text-surface-900 dark:text-surface-100 truncate" title={file.originalName}>
                    {file.originalName}
                  </h5>
                  <span className="text-[10px] text-surface-400 block">
                    {formatFileSize(file.fileSize)} • by {file.uploaderName}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions Bar */}
            <div className="pt-3 border-t border-surface-100 dark:border-surface-800 flex items-center justify-between text-xs">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPreviewFile(file)}
                  className="flex items-center space-x-1 px-2.5 py-1 rounded-lg text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-950/40 hover:bg-brand-100 dark:hover:bg-brand-900/50 transition-colors font-semibold"
                  title="Preview Document"
                >
                  <Eye className="w-3.5 h-3.5 mr-1" />
                  <span>Preview</span>
                </button>
                <button
                  onClick={() => setProofingFile(file)}
                  className="px-2 py-1 rounded-lg text-surface-600 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors font-medium text-[11px]"
                  title="Visual Proofing & Version Stacking"
                >
                  Proof & Versions
                </button>
                <a
                  href={`/api/files/${file.id}/download`}
                  download={file.originalName}
                  className="p-1.5 rounded-lg text-surface-500 hover:text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-950/40 transition-colors"
                  title="Download Raw File"
                >
                  <Download className="w-4 h-4" />
                </a>
              </div>

              {(user?.role === 'admin' || user?.role === 'lead' || user?.id === file.uploadedBy) && (
                <button
                  onClick={() => handleDeleteFile(file.id)}
                  className="p-1.5 rounded-lg text-surface-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors"
                  title="Delete file"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {files.length === 0 && (
        <div className="py-12 text-center text-xs text-surface-400 bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-800">
          No files uploaded for this project yet. Upload specifications, assets, or deliverables above.
        </div>
      )}

      {/* Visual Proofing Modal */}
      {proofingFile && (
        <FileProofingModal
          file={proofingFile}
          isOpen={true}
          onClose={() => setProofingFile(null)}
        />
      )}

      {/* Interactive Document Preview Modal */}
      {previewFile && (
        <DocumentPreviewModal
          file={previewFile}
          isOpen={true}
          onClose={() => setPreviewFile(null)}
        />
      )}
    </div>
  );
};
